import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';

import CurrentUserContext from 'contexts/CurrentUserContext';
import usePerformanceFieldsOptions from 'components/PerformanceBoards/hooks/usePerformanceFieldsOptions';
import getTimeStamp from 'getTimeStamp';
import usePerformanceSlideOutFilterInput from 'components/PerformanceBoards/hooks/usePerformanceSlideOutFilterInput';
import useSlideoutDateScope from 'components/PerformanceBoards/hooks/useSlideoutDateScope';
import useSaveWidget from 'components/PerformanceBoards/hooks/useSaveWidget';
import useReducerProps from './reducer/useReducerProps';
import PerformanceSlideoutWizard from './PerformanceSlideoutWizard';
import PerformanceBoardSlideOutContext from '../../../contexts/PerformanceBoardSlideOutContext';

const PerformanceSlideoutWizardContainer = ({
  close,
  editingSection,
}: {
  close: () => void;
  editingSection: PerformanceBoardTypes.SlideOut.PerformanceSection | undefined;
}) => {
  const { board } = useContext(PerformanceBoardSlideOutContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { performanceReducer, initialState } = useReducerProps(editingSection);
  const [widgetDateRange, setWidgetDateRange] =
    useState<PerformanceBoardTypes.SlideOut.SlideOutDateRange>(
      editingSection
        ? editingSection.dateRange
        : { mode: 'Current Interval', isToDate: false },
    );
  const [state, dispatch] = useReducer(performanceReducer, initialState);

  const filterInput = usePerformanceSlideOutFilterInput({
    datasetForDefaultFiltering: board.dataType,
  });
  const dateScope = useSlideoutDateScope(state.widgetDateRange);
  const saveWidget = useSaveWidget({
    section: state.performanceSection,
    editingSection,
  });

  const { fieldsOptions, selectedField, isPerformanceFieldRemoved } =
    usePerformanceFieldsOptions({
      initialSelectedField: editingSection
        ? editingSection.performanceField
        : undefined,
    });

  const onTitleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    dispatch({
      type: 'UPDATE TITLE' as 'UPDATE TITLE',
      payload: { newTitle: event.target.value },
    });
  };

  const isDisabled = state.title === '' || !selectedField;

  useEffect(() => {
    if (isDisabled) {
      dispatch({
        type: 'UPDATE PERFORMANCE SECTION' as 'UPDATE PERFORMANCE SECTION',
        payload: { newSection: undefined },
      });
      return;
    }

    const newPerformanceSection = {
      id: state.id,
      createdBy: state.createdBy,
      updatedBy: currentUserId,
      createdOn: state.createdOn,
      updatedOn: getTimeStamp(),
      sectionType: 'Performance Graph' as 'Performance Graph',
      title: state.title,
      performanceField: selectedField,
      dateRange: state.widgetDateRange,
      isReasonCodeEnabled: state.showReasonCodes,
      isGridHidden: state.isGridHidden,
    };

    dispatch({
      type: 'UPDATE PERFORMANCE SECTION' as 'UPDATE PERFORMANCE SECTION',
      payload: { newSection: newPerformanceSection },
    });
  }, [
    currentUserId,
    isDisabled,
    selectedField,
    state.createdBy,
    state.createdOn,
    state.id,
    state.isGridHidden,
    state.showReasonCodes,
    state.title,
    state.widgetDateRange,
  ]);

  /**
   * Jank here to deal with types as a component downstream wants a useState
   * updater function
   */
  useEffect(() => {
    dispatch({
      type: 'UPDATE WIDGET DATE RANGE' as 'UPDATE WIDGET DATE RANGE',
      payload: {
        newWidgetDateRange: widgetDateRange,
      },
    });
  }, [widgetDateRange]);

  return (
    <PerformanceSlideoutWizard
      saveWidget={saveWidget}
      onTitleChanged={onTitleChanged}
      setWidgetDateRange={setWidgetDateRange}
      isDisabled={isDisabled}
      filterInput={filterInput}
      dateScope={dateScope}
      state={state}
      dispatch={dispatch}
      isPerformanceFieldRemoved={isPerformanceFieldRemoved}
      fieldsOptions={fieldsOptions}
      close={close}
    />
  );
};

export default PerformanceSlideoutWizardContainer;
